/**
 * The weekly trash-talk board: what the buddies say to each other under each
 * week's games. Pure types + constants, shared by the API (which validates and
 * persists) and the web app (which renders the buttons). ESPN knows nothing
 * about this — it's the part of the league that lives in the group text.
 */
/**
 * The reaction palette, in display order — a curated set the picker shows as a
 * grid. On-brand smack (the cap = calling BS, the clown, the skull) plus the
 * staples people actually reach for: heart, football, thumbs, the GOAT. Adding
 * one here offers it everywhere.
 */
export const REACTIONS = [
    '😂', '❤️', '🔥', '🏈', '👍', '👎', '🐐', '👀',
    '😭', '🧢', '🤡', '💀', '💩', '🙏', '💯', '🫡',
];
export function isReaction(s) {
    return REACTIONS.includes(s);
}
/** The most characters one post may carry. Enough for a paragraph of smack. */
export const MAX_POST_LENGTH = 500;
/**
 * The sentinel "week" for a season's pre-season / draft board. ESPN scoring
 * weeks start at 1, so week 0 never collides with a real game week — it's a
 * board that exists before a single snap, to talk draft and keepers and stir
 * the pot. Same storage, routes and transactions as any weekly board; only the
 * key differs.
 */
export const PRESEASON_WEEK = 0;
/** Total reactions on a post: the sum of every emoji's tally. */
export function reactionCount(post) {
    return Object.values(post.reactions).reduce((n, who) => n + who.length, 0);
}
/**
 * Toggle one person's reaction, returning a fresh reactions map: the emoji is
 * added if they hadn't used it and removed if they had. An emoji whose last
 * reactor leaves is dropped entirely, so the map never carries empty tallies
 * (which would render as a "0" chip). Pure so it can be reasoned about and
 * tested without a database; the store persists whatever it returns.
 */
export function toggleReaction(reactions, emoji, reactor) {
    const who = reactions[emoji] ?? [];
    const has = who.some((r) => r.email === reactor.email);
    const next = has ? who.filter((r) => r.email !== reactor.email) : [...who, reactor];
    const out = { ...reactions };
    if (next.length > 0)
        out[emoji] = next;
    else
        delete out[emoji];
    return out;
}
/**
 * Coerce a reactions map into the current shape, resolving each reactor's
 * display name. `nameByEmail` (the league roster, keyed by lowercase email) is
 * preferred, so a real name like "Morgan Ellis" always wins over a stored or
 * derived one — this repairs legacy reactions that saved only the email and
 * keeps names current if someone is renamed. Falls back to a stored name, then
 * to one derived from the email, so a reactor never renders as nothing.
 */
export function normalizeReactions(raw, nameByEmail) {
    const out = {};
    if (!raw || typeof raw !== 'object')
        return out;
    for (const [emoji, list] of Object.entries(raw)) {
        if (!Array.isArray(list))
            continue;
        const who = list.map((item) => {
            const email = typeof item === 'string' ? item : String(item.email ?? '');
            const stored = typeof item === 'string' ? '' : String(item.name ?? '');
            const name = nameByEmail?.[email.toLowerCase()] || stored || nameFromEmail(email);
            return { email, name };
        });
        if (who.length > 0)
            out[emoji] = who;
    }
    return out;
}
/** Best-effort display name from an email local part: "jane.doe" -> "Jane Doe". */
function nameFromEmail(email) {
    const local = email.split('@')[0] ?? email;
    const pretty = local
        .split(/[._-]+/)
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    return pretty || email;
}
/**
 * How many posts by other people a member hasn't seen, given the newest post
 * they have seen (an ISO timestamp, the board's high-water mark for them).
 * Null means they have never opened the board, so everything by others counts.
 * Strictly newer: the marker itself is the last post they saw.
 */
export function unreadCount(posts, lastReadAt, myEmail) {
    const me = myEmail.toLowerCase();
    const since = lastReadAt ? Date.parse(lastReadAt) : Number.NEGATIVE_INFINITY;
    let n = 0;
    for (const p of posts) {
        if (p.authorEmail.toLowerCase() === me)
            continue;
        if (Date.parse(p.createdAt) > since)
            n += 1;
    }
    return n;
}
/** The newest post's timestamp on a board, or null when it's empty. The max, not the last. */
export function newestPostAt(posts) {
    let best = null;
    for (const p of posts) {
        if (best === null || Date.parse(p.createdAt) > Date.parse(best))
            best = p.createdAt;
    }
    return best;
}
//# sourceMappingURL=trash.js.map